import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Image, Progress } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import videoPreviewDefault from "../../assets/video_preview_default.jpg";
import { formatTimeBySeconds } from "../../utils/formatTime";
import { standardizeVolume } from "../../utils/Utils";
import { useGlobalDownloader } from '../../context/global/DownloadProvider';
import { useMediaResource } from "../../hooks/useMediaResource";
import MediaViewerModal from "../modal/MediaViewerModal";

const PreviewVideoContent = ({ file, isSelf, isInPanel }) => {
  const mediaResource = useMediaResource();
  const containerStyle = useMemo(() => {
    const originalWidth = file?.width || 16;
    const originalHeight = file?.height || 9;
    const targetHeight = 250;

    const aspectRatio = originalWidth / originalHeight;
    const targetWidth = targetHeight * aspectRatio;

    return {
      // height: `${targetHeight}px`,
      maxHeight: "250px",
      width: `${targetWidth}px`,
      maxWidth: "380px",
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f0f0f0",
      borderRadius: "8px",
      overflow: "hidden",
    };
  }, [file?.width, file?.height]);

  const containerPanelStyle = useMemo(() => {
    return {
      height: `40px`,
      width: `40px`,
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f0f0f0",
      borderRadius: "8px",
      overflow: "hidden",
      border: "1px solid #d9d9d9",
      margin: "0px 8px",
    };
  }, []);

  // 1. Dùng useGlobalDownloader
  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  // 2. Lấy trạng thái của file này theo MinIO path
  const [fileState, setFileState] = useState({});
  const fileNameToSave = file?.originalName || "Willo-video";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(file?.mainPath, file?.originalName);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(file?.mainPath);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(file?.mainPath, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(file?.mainPath);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <div className="flex gap-2">
            <button onClick={handlePauseDownload}>
              <PauseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "#2b9853" }} />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "#2b9853" }} />
          </button>
        );
    }
  }, [fileState?.status]);

  // 3. Đã XÓA useEffect chứa logic gọi saveFile() vì giờ Provider tự động download.

  const subOriginalName = () => {
    const maxLength = 15;
    let newOriginalName;

    if (file?.originalName?.trim()?.length > maxLength + 7) {
      newOriginalName =
        file?.originalName?.slice(0, maxLength) +
        "..." +
        file?.originalName?.slice(-7);
    } else {
      newOriginalName = file?.originalName;
    }

    return newOriginalName?.trim() || "Unknown.file";
  };

  const renderContent = useMemo(() => {
    const imageClassName = "cursor-pointer w-full h-full object-cover";

    return (
      <Image
        height={isInPanel && "100%"}
        width={isInPanel && "100%"}
        src={file?.videoImgPreviewPath || videoPreviewDefault}
        preview={false}
        className={imageClassName}
      />
    );
  }, [file?.videoImgPreviewPath, isInPanel]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [fileState?.progress]);

  useEffect(() => {
    setFileState(downloads[file?.mainPath]);
  }, [downloads, file?.mainPath]);

  return (
    <div className="flex flex-col max-w-full">
      <button
        className={`flex mb-[2px] w-full mt-[4px]`}
        style={{
          justifyContent: `${isSelf ? "flex-end" : "flex-start"}`,
          margin: !isInPanel && "auto",
        }}
        onClick={() => {
          mediaResource.setSelectedMedia({
            ...file,
            createdAt: file?.createdAt,
            userId: file?.senderId,
          });
          mediaResource.setMediaTypes(["VIDEO"]);
          mediaResource.setIsFetchData(true);
          mediaResource.setVisible(true);
        }}
      >
        <div style={isInPanel ? containerPanelStyle : containerStyle}>
          <div
            className={`left-duration ${
              isInPanel ? "top-[10px] left-[0]" : ""
            }`}
          >
            {!isInPanel && formatTimeBySeconds(Math.round(file?.duration))}
          </div>

          {renderContent}

          <div className="center-play">
            <FaPlayCircle size={isInPanel ? 10 : 50} />
          </div>
        </div>
        {isInPanel && (
          <div className="text-[12px] flex flex-1 flex-col justify-center items-start ">
            <div>{subOriginalName()}</div>
            <div className="flex w-full gap-[10px] justify-between file-info">
              <div className="flex gap-[5px]">
                <span>{(file?.volume / (1024 * 1024)).toFixed(2)} MB </span>{" "}
                <span>-</span>
                <span>{formatTimeBySeconds(Math.round(file?.duration))}</span>
              </div>

              <span className="me-[38px]">{renderDownloadButton()}</span>
            </div>
          </div>
        )}
      </button>

      {/* 4. Cập nhật kiểm tra trạng thái */}
      {[DOWNLOAD_STATUS.DOWNLOADING].includes(fileState?.status) &&
        isInPanel && (
          <div className="flex items-center gap-[2px]">
            <Progress
              className="flex-1"
              percent={fileState?.progress}
              showInfo={false}
              strokeColor="#52c41a"
              size="small"
            />
            {progressRate}
          </div>
        )}

      {/* Modal view image */}
      {mediaResource.visible && (
        <MediaViewerModal mediaResource={mediaResource} />
      )}
    </div>
  );
};
export { PreviewVideoContent };

